import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AppLoader from '../../../components/AppLoader';
import { fetchJson, resolveApiUrl } from '../../../utils/fetchJson';

export default function ManageFeePaymentsPage() {
  const { t, i18n } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [payments, setPayments] = useState([]);

  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedFeeStructureId, setSelectedFeeStructureId] = useState('');
  const [selectedInstallmentId, setSelectedInstallmentId] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [tab, setTab] = useState('status');
  const [statusTab, setStatusTab] = useState('all');
  const [studentSearch, setStudentSearch] = useState('');

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Parent-submitted offline (bank / mobile money) payments awaiting review
  const [reviewPayments, setReviewPayments] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [actionBusyKey, setActionBusyKey] = useState('');
  const [approveGroupState, setApproveGroupState] = useState(null); // { group, rows: [{id, feeName, amount, paidBefore, required}] }
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectGroup, setRejectGroup] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        // Context key and cache prefill
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${ayId || '-'}`;

        try { const r = localStorage.getItem(`finance:classes:${ctxKey}`); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) setClasses(v); } } catch(_) {}
        try { const r = localStorage.getItem(`finance:students:${ctxKey}`); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) setStudents(v); } } catch(_) {}
        try { const r = localStorage.getItem(`finance:feeStructures:${ctxKey}`); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) setFeeStructures(v); } } catch(_) {}

        let maxGuard; try { maxGuard = setTimeout(() => setLoading(false), 2000); } catch(_) {}

        const [classesRes, studentsRes, feeStructuresRes] = await Promise.all([
          fetchJson('/api/classes/for-allocations', { method: 'GET' }),
          fetchJson('/api/students/for-allocations', { method: 'GET' }),
          fetchJson('/api/finance/fee-structures', { method: 'GET' }),
        ]);
        const cls = Array.isArray(classesRes) ? classesRes : (classesRes.classes || classesRes.data || []);
        const std = Array.isArray(studentsRes) ? studentsRes : (studentsRes.students || studentsRes.data || []);
        const fs = feeStructuresRes.data || feeStructuresRes.structures || [];
        setClasses(cls);
        setStudents(std);
        setFeeStructures(fs);
        try { localStorage.setItem(`finance:classes:${ctxKey}`, JSON.stringify(cls)); } catch(_) {}
        try { localStorage.setItem(`finance:students:${ctxKey}`, JSON.stringify(std)); } catch(_) {}
        try { localStorage.setItem(`finance:feeStructures:${ctxKey}`, JSON.stringify(fs)); } catch(_) {}

        setLoading(false);
        try { clearTimeout(maxGuard); } catch(_) {}
      } catch (e) {
        setSnackbar({ open: true, message: t('manageFeePaymentsPage.errors.loadInitial'), severity: 'error' });
        setLoading(false);
      }
    };
    init();
  }, []);

  const filteredFeeStructures = useMemo(() => {
    return (feeStructures || []).filter(fs =>
      (!fs?.status || fs.status === 'active') &&
      (!selectedClassId || String(fs.classId) === String(selectedClassId))
    );
  }, [feeStructures, selectedClassId]);

  const filteredStudents = useMemo(() => {
    return students.filter(s => !selectedClassId || String(s.classId) === String(selectedClassId));
  }, [students, selectedClassId]);

  const selectedFeeStructure = useMemo(() => {
    return feeStructures.find(fs => String(fs.id) === String(selectedFeeStructureId)) || null;
  }, [feeStructures, selectedFeeStructureId]);

  const selectedInstallment = useMemo(() => {
    const list = Array.isArray(selectedFeeStructure?.installments) ? selectedFeeStructure.installments : [];
    return list.find(i => String(i.id) === String(selectedInstallmentId)) || null;
  }, [selectedFeeStructure, selectedInstallmentId]);

  const hasInstallments = !!(selectedFeeStructure?.hasInstallments && Array.isArray(selectedFeeStructure?.installments) && selectedFeeStructure.installments.length > 0);

  const loadPayments = async () => {
    if (!selectedClassId || !selectedFeeStructureId) { setPayments([]); return; }
    try {
      const qs = new URLSearchParams({
        classId: String(selectedClassId),
        feeStructureId: String(selectedFeeStructureId),
        status: 'completed',
        pageSize: '100'
      });
      if (selectedStudentId) qs.set('studentId', String(selectedStudentId));
      const res = await fetchJson(`/api/finance/payments?${qs.toString()}`, { method: 'GET' });
      const list = res.data || res.payments || [];
      setPayments(Array.isArray(list) ? list : []);
    } catch (e) {
      setSnackbar({ open: true, message: t('manageFeePaymentsPage.errors.loadPayments'), severity: 'error' });
    }
  };

  useEffect(() => {
    // whenever class/filters change, reload
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId, selectedFeeStructureId, selectedStudentId]);

  const loadReviewPayments = async () => {
    setReviewLoading(true);
    try {
      const res = await fetchJson('/api/finance/payments?status=pending&provider=offline&pageSize=100', { method: 'GET' });
      const list = res.data || res.payments || [];
      setReviewPayments(Array.isArray(list) ? list : []);
    } catch (e) {
      setSnackbar({ open: true, message: t('manageFeePaymentsPage.review.loadFailed', 'Failed to load submissions'), severity: 'error' });
    } finally {
      setReviewLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'review') loadReviewPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Group pending offline payments by their shared proof (one parent submission = one group).
  const reviewGroups = useMemo(() => {
    const map = new Map();
    for (const p of (reviewPayments || [])) {
      const key = p.proofUrl ? `proof:${p.proofUrl}` : `pay:${p.id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          proofUrl: p.proofUrl || null,
          studentName: p.Student ? `${p.Student.firstName || ''} ${p.Student.lastName || ''}`.trim() : '-',
          className: p.Class ? p.Class.name : '',
          payerName: p.payerName || '',
          reference: p.providerReference || '',
          createdAt: p.createdAt,
          rows: [],
          total: 0,
          paymentIds: []
        });
      }
      const g = map.get(key);
      g.rows.push({
        id: p.id,
        feeName: p.FeeStructure ? p.FeeStructure.name : '-',
        amount: Number(p.amount || 0),
        studentId: p.studentId || p.Student?.id || null,
        feeStructureId: p.feeStructureId || p.FeeStructure?.id || null,
        feeAmount: Number(p.FeeStructure?.amount || 0),
      });
      g.total += Number(p.amount || 0);
      g.paymentIds.push(p.id);
    }
    return Array.from(map.values()).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [reviewPayments]);

  // A parent submits a PROOF FILE, not a figure. When their app sends no
  // amount the server fills in the whole outstanding balance (see
  // portalParentController), so a part payment can arrive claiming the full
  // fee. Approving it blindly credits money the school never received.
  //
  // So approval opens a dialog showing the proof beside an EDITABLE AMOUNT per
  // fee, and states what each one would leave outstanding. The amount is the
  // only figure a human enters; the balance is derived from the ledger and can
  // never be typed into disagreement with it.
  const openApprove = async (group) => {
    setApproveGroupState({ group, rows: group.rows.map(r => ({ ...r, paidBefore: null })) });
    const rows = await Promise.all(group.rows.map(async (r) => {
      if (!r.studentId || !r.feeStructureId) return { ...r, paidBefore: 0 };
      try {
        const res = await fetchJson(`/api/finance/payments?studentId=${encodeURIComponent(r.studentId)}&feeStructureId=${encodeURIComponent(r.feeStructureId)}&status=completed&pageSize=100`, { method: 'GET' });
        const list = Array.isArray(res) ? res : (res?.data || res?.payments || res?.rows || []);
        // This payment is still pending, so it is not in that sum.
        const paidBefore = (Array.isArray(list) ? list : [])
          .filter(x => String(x.id) !== String(r.id))
          .reduce((n, x) => n + Number(x.amount || 0), 0);
        return { ...r, paidBefore };
      } catch (e) { return { ...r, paidBefore: 0 }; }
    }));
    setApproveGroupState(prev => (prev && prev.group.key === group.key ? { ...prev, rows } : prev));
  };

  const approveGroup = async (group, rows) => {
    setActionBusyKey(group.key);
    try {
      // Only genuine corrections travel; an untouched amount is left alone.
      const amounts = {};
      for (const r of (rows || [])) {
        const v = Number(r.amount);
        const original = (group.rows.find(x => String(x.id) === String(r.id)) || {}).amount;
        if (Number.isFinite(v) && v > 0 && v !== Number(original)) amounts[r.id] = v;
      }
      await fetchJson('/api/finance/payments/offline/approve', {
        method: 'POST',
        body: JSON.stringify({
          paymentIds: group.paymentIds,
          ...(Object.keys(amounts).length ? { amounts } : {}),
        }),
      });
      setApproveGroupState(null);
      setSnackbar({ open: true, message: t('manageFeePaymentsPage.review.approved', 'Payment approved and recorded'), severity: 'success' });
      await loadReviewPayments();
      await loadPayments();
    } catch (e) {
      setSnackbar({ open: true, message: e?.message || t('manageFeePaymentsPage.review.approveFailed', 'Failed to approve'), severity: 'error' });
    } finally {
      setActionBusyKey('');
    }
  };

  const openReject = (group) => { setRejectGroup(group); setRejectReason(''); setRejectOpen(true); };
  const confirmReject = async () => {
    if (!rejectGroup) return;
    setActionBusyKey(rejectGroup.key);
    try {
      await fetchJson('/api/finance/payments/offline/reject', { method: 'POST', body: JSON.stringify({ paymentIds: rejectGroup.paymentIds, reason: rejectReason || null }) });
      setRejectOpen(false);
      setRejectGroup(null);
      setRejectReason('');
      setSnackbar({ open: true, message: t('manageFeePaymentsPage.review.rejected', 'Payment rejected'), severity: 'info' });
      await loadReviewPayments();
    } catch (e) {
      setSnackbar({ open: true, message: e?.message || t('manageFeePaymentsPage.review.rejectFailed', 'Failed to reject'), severity: 'error' });
    } finally {
      setActionBusyKey('');
    }
  };

  const totalPaid = useMemo(() => payments.reduce((s, p) => s + Number(p.amount || 0), 0), [payments]);

  const paymentTotalsByStudentId = useMemo(() => {
    const map = new Map();
    for (const p of (payments || [])) {
      const sid = p?.studentId;
      if (!sid) continue;
      map.set(String(sid), Number(map.get(String(sid)) || 0) + Number(p.amount || 0));
    }
    return map;
  }, [payments]);

  const studentStatusRows = useMemo(() => {
    if (!selectedClassId || !selectedFeeStructureId) return [];
    if (!selectedFeeStructure) return [];
    if (hasInstallments && !selectedInstallmentId) return [];

    const all = filteredStudents
      .filter(s => !selectedStudentId || String(s.id) === String(selectedStudentId))
      .map(s => {
        const studentName = `${s.firstName || ''} ${s.lastName || ''}`.trim() || '-';
        const totalPaid = Number(paymentTotalsByStudentId.get(String(s.id)) || 0);

        // Non-installment
        if (!hasInstallments) {
          const required = Number(selectedFeeStructure.amount || 0);
          const paid = totalPaid;
          const unpaid = Math.max(required - paid, 0);
          let status = 'not_paid';
          if (paid > 0 && unpaid > 0) status = 'partially_paid';
          if (required > 0 && unpaid <= 0 && paid > 0) status = 'paid';
          return {
            studentId: s.id,
            studentNumber: s.studentNumber || null,
            studentName,
            classId: selectedClassId,
            feeStructureId: selectedFeeStructure.id,
            feeName: selectedFeeStructure.name,
            required,
            paid,
            unpaid,
            status
          };
        }

        // Installment-based
        const installments = Array.isArray(selectedFeeStructure.installments) ? [...selectedFeeStructure.installments] : [];
        installments.sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0));
        const idx = installments.findIndex(i => String(i.id) === String(selectedInstallmentId));
        const safeIdx = idx >= 0 ? idx : 0;
        const prevSum = installments.slice(0, safeIdx).reduce((sum, i) => sum + Number(i.amount || 0), 0);
        const installmentAmount = Number(installments[safeIdx]?.amount || 0);

        const paidForThis = Math.min(Math.max(totalPaid - prevSum, 0), installmentAmount);
        const unpaid = Math.max(installmentAmount - paidForThis, 0);
        let status = 'not_paid';
        if (paidForThis > 0 && unpaid > 0) status = 'partially_paid';
        if (installmentAmount > 0 && unpaid <= 0 && paidForThis > 0) status = 'paid';

        return {
          studentId: s.id,
          studentNumber: s.studentNumber || null,
          studentName,
          classId: selectedClassId,
          feeStructureId: selectedFeeStructure.id,
          feeName: selectedFeeStructure.name,
          installmentId: selectedInstallmentId,
          installmentName: selectedInstallment?.name || installments[safeIdx]?.name || null,
          required: installmentAmount,
          paid: paidForThis,
          unpaid,
          status
        };
      });

    all.sort((a, b) => String(a.studentName || '').localeCompare(String(b.studentName || '')));
    return all;
  }, [
    selectedClassId,
    selectedFeeStructureId,
    selectedFeeStructure,
    filteredStudents,
    selectedStudentId,
    hasInstallments,
    selectedInstallmentId,
    selectedInstallment,
    paymentTotalsByStudentId
  ]);

  const visibleStatusRows = useMemo(() => {
    const q = String(studentSearch || '').trim().toLowerCase();
    return studentStatusRows.filter(r => {
      if (statusTab !== 'all' && r.status !== statusTab) return false;
      if (q && !String(r.studentName || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [studentStatusRows, statusTab, studentSearch]);

  const statusCounts = useMemo(() => {
    const base = { paid: 0, partially_paid: 0, not_paid: 0, all: studentStatusRows.length };
    for (const r of studentStatusRows) {
      if (r.status === 'paid') base.paid += 1;
      else if (r.status === 'partially_paid') base.partially_paid += 1;
      else base.not_paid += 1;
    }
    return base;
  }, [studentStatusRows]);

  const exportRows = (rows, mode) => {
    const className = classes.find(c => String(c.id) === String(selectedClassId))?.name || '';
    const feeName = feeStructures.find(fs => String(fs.id) === String(selectedFeeStructureId))?.name || '';
    const installmentName = selectedInstallment?.name || '';
    if (mode === 'print') {
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      const tableHtml = `
        <h2>${t('manageFeePaymentsPage.export.print.title')}</h2>
        <div style="color:#6b7280;font-size:12px;margin-bottom:12px;">${t('manageFeePaymentsPage.labels.class')}: ${className || '-'}<br/>${t('manageFeePaymentsPage.labels.feeStructure')}: ${feeName || '-'}${installmentName ? `<br/>${t('manageFeePaymentsPage.labels.installment')}: ${installmentName}` : ''}<br/>${t('manageFeePaymentsPage.labels.students')}: ${rows.length}</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr>
              <th style="border:1px solid #d1d5db;padding:8px;text-align:left;background:#f3f4f6;">${t('manageFeePaymentsPage.tableStatus.headers.studentNo')}</th>
              <th style="border:1px solid #d1d5db;padding:8px;text-align:left;background:#f3f4f6;">${t('manageFeePaymentsPage.tableStatus.headers.student')}</th>
              <th style="border:1px solid #d1d5db;padding:8px;text-align:right;background:#f3f4f6;">${t('manageFeePaymentsPage.tableStatus.headers.required')}</th>
              <th style="border:1px solid #d1d5db;padding:8px;text-align:right;background:#f3f4f6;">${t('manageFeePaymentsPage.tableStatus.headers.paid')}</th>
              <th style="border:1px solid #d1d5db;padding:8px;text-align:right;background:#f3f4f6;">${t('manageFeePaymentsPage.tableStatus.headers.unpaid')}</th>
              <th style="border:1px solid #d1d5db;padding:8px;text-align:left;background:#f3f4f6;">${t('manageFeePaymentsPage.tableStatus.headers.status')}</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(r => `
              <tr>
                <td style="border:1px solid #d1d5db;padding:8px;">${String(r.studentNumber || '-')}</td>
                <td style="border:1px solid #d1d5db;padding:8px;">${String(r.studentName || '-')}</td>
                <td style="border:1px solid #d1d5db;padding:8px;text-align:right;">${Number(r.required || 0).toLocaleString(i18n.language || undefined)} XAF</td>
                <td style="border:1px solid #d1d5db;padding:8px;text-align:right;">${Number(r.paid || 0).toLocaleString(i18n.language || undefined)} XAF</td>
                <td style="border:1px solid #d1d5db;padding:8px;text-align:right;">${Number(r.unpaid || 0).toLocaleString(i18n.language || undefined)} XAF</td>
                <td style="border:1px solid #d1d5db;padding:8px;">${r.status === 'paid'
                  ? t('manageFeePaymentsPage.status.paid')
                  : r.status === 'partially_paid'
                    ? t('manageFeePaymentsPage.status.partially_paid')
                    : t('manageFeePaymentsPage.status.not_paid')}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
      printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${t('manageFeePaymentsPage.export.print.windowTitle')}</title><style>body{font-family:Arial,sans-serif;padding:20px;}</style></head><body>${tableHtml}</body></html>`);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
      return;
    }

    const header = [
      t('manageFeePaymentsPage.tableStatus.headers.studentNo'),
      t('manageFeePaymentsPage.tableStatus.headers.student'),
      t('manageFeePaymentsPage.tableStatus.headers.required'),
      t('manageFeePaymentsPage.tableStatus.headers.paid'),
      t('manageFeePaymentsPage.tableStatus.headers.unpaid'),
      t('manageFeePaymentsPage.tableStatus.headers.status')
    ];
    const lines = [header.join(',')];
    for (const r of rows) {
      const status = r.status === 'paid'
        ? t('manageFeePaymentsPage.status.paid')
        : r.status === 'partially_paid'
          ? t('manageFeePaymentsPage.status.partially_paid')
          : t('manageFeePaymentsPage.status.not_paid');
      const values = [
        `"${String(r.studentNumber || '').replaceAll('"', '""')}"`,
        `"${String(r.studentName || '').replaceAll('"', '""')}"`,
        Number(r.required || 0),
        Number(r.paid || 0),
        Number(r.unpaid || 0),
        `"${status}"`
      ];
      lines.push(values.join(','));
    }
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeClass = String(className || t('manageFeePaymentsPage.export.fallbacks.class')).replaceAll(' ', '_');
    const safeFee = String(feeName || t('manageFeePaymentsPage.export.fallbacks.fee')).replaceAll(' ', '_');
    const safeInst = String(installmentName || '').replaceAll(' ', '_');
    a.href = url;
    a.download = `${t('manageFeePaymentsPage.export.csv.filenamePrefix')}-${safeClass}-${safeFee}${safeInst ? `-${safeInst}` : ''}-${statusTab}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <AppLoader
      variant="circular3"
      size={58}
      centered
      minHeight="50vh"
      messages={[
        t('manageFeePaymentsPage.loading.finance'),
        t('manageFeePaymentsPage.loading.classesStudents'),
        t('manageFeePaymentsPage.loading.applyingPhaseAy')
      ]}
    />
  );

  return (
    <Box p={2}>
      {/* No title card. This page is only ever rendered inside FeesPage, whose
          tab already says which half of it you are on — and the title said
          "Manage Fee Payments", the name of a page that no longer exists on its
          own. What it explained is kept, as one quiet line. */}
      <Typography variant="body2" color="text.secondary">
        {t('manageFeePaymentsPage.subheader')}
      </Typography>

      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="class-label">{t('manageFeePaymentsPage.selects.class')}</InputLabel>
              <Select
                labelId="class-label"
                label={t('manageFeePaymentsPage.selects.class')}
                value={selectedClassId}
                onChange={(e) => {
                  const nextClassId = e.target.value;
                  setSelectedClassId(nextClassId);
                  setSelectedFeeStructureId('');
                  setSelectedInstallmentId('');
                  setSelectedStudentId('');
                  setStudentSearch('');
                  setStatusTab('all');
                }}
              >
                <MenuItem value="" disabled>{t('manageFeePaymentsPage.selects.selectClass')}</MenuItem>
                {classes.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="fee-label">{t('manageFeePaymentsPage.selects.feeStructure')}</InputLabel>
              <Select
                labelId="fee-label"
                label={t('manageFeePaymentsPage.selects.feeStructure')}
                value={selectedFeeStructureId}
                onChange={(e) => {
                  const nextFsId = e.target.value;
                  const fs = feeStructures.find(x => String(x.id) === String(nextFsId));
                  setSelectedFeeStructureId(nextFsId);
                  setSelectedInstallmentId('');
                  setSelectedStudentId('');
                  setStudentSearch('');
                  setStatusTab('all');
                  if (fs?.classId) {
                    setSelectedClassId(String(fs.classId));
                  }
                }}
              >
                <MenuItem value="" disabled>{t('manageFeePaymentsPage.selects.selectFeeStructure')}</MenuItem>
                {filteredFeeStructures.map(fs => (
                  <MenuItem key={fs.id} value={fs.id}>
                    {fs.name}{fs.Class?.name ? ` — ${fs.Class.name}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {hasInstallments && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="installment-label">{t('manageFeePaymentsPage.selects.installment')}</InputLabel>
                <Select
                  labelId="installment-label"
                  label={t('manageFeePaymentsPage.selects.installment')}
                  value={selectedInstallmentId}
                  onChange={(e) => {
                    setSelectedInstallmentId(e.target.value);
                    setSelectedStudentId('');
                    setStudentSearch('');
                    setStatusTab('all');
                  }}
                >
                  <MenuItem value="" disabled>{t('manageFeePaymentsPage.selects.selectInstallment')}</MenuItem>
                  {(selectedFeeStructure?.installments || []).map(inst => (
                    <MenuItem key={inst.id} value={inst.id}>
                      {inst.name} ({Number(inst.amount || 0).toLocaleString(i18n.language || undefined)} XAF)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="student-label">{t('manageFeePaymentsPage.selects.student')}</InputLabel>
              <Select labelId="student-label" label={t('manageFeePaymentsPage.selects.student')} value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)}>
                <MenuItem value="">{t('manageFeePaymentsPage.selects.all')}</MenuItem>
                {filteredStudents.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName}{s.studentNumber ? ` (${s.studentNumber})` : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box mt={2}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label={t('manageFeePaymentsPage.tabs.status')} value="status" />
          <Tab label={t('manageFeePaymentsPage.tabs.payments')} value="payments" />
          {/* The review tab lived here AND on Payment Processing, so two
              people could approve one parent's receipt. Approval now belongs to
              the Payments & receipts half of the merged Fees page, and this
              half answers only "who owes what". The loader and the group
              builder are kept: they are what that half uses. */}
        </Tabs>
      </Box>

      {tab === 'status' && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
              <Typography variant="h6">{t('manageFeePaymentsPage.headers.studentFeePaymentStatus')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button variant="outlined" disabled={!selectedClassId || !selectedFeeStructureId} onClick={() => exportRows(visibleStatusRows, 'print')}>{t('manageFeePaymentsPage.actions.print')}</Button>
                <Button variant="contained" disabled={!selectedClassId || !selectedFeeStructureId} onClick={() => exportRows(visibleStatusRows, 'download')}>{t('manageFeePaymentsPage.actions.downloadCsv')}</Button>
              </Box>
            </Box>

            {!selectedClassId || !selectedFeeStructureId ? (
              <Typography variant="body2" color="text.secondary">
                {t('manageFeePaymentsPage.prompts.selectClassFee')}
              </Typography>
            ) : (hasInstallments && !selectedInstallmentId) ? (
              <Typography variant="body2" color="text.secondary">
                {t('manageFeePaymentsPage.prompts.selectInstallment')}
              </Typography>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('manageFeePaymentsPage.searchStudent')}
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                    />
                  </Grid>
                </Grid>

                <Tabs value={statusTab} onChange={(e, v) => setStatusTab(v)} sx={{ mb: 2 }}>
                  <Tab value="all" label={t('manageFeePaymentsPage.statusTabs.all', { count: statusCounts.all })} />
                  <Tab value="paid" label={t('manageFeePaymentsPage.statusTabs.paid', { count: statusCounts.paid })} />
                  <Tab value="partially_paid" label={t('manageFeePaymentsPage.statusTabs.partially_paid', { count: statusCounts.partially_paid })} />
                  <Tab value="not_paid" label={t('manageFeePaymentsPage.statusTabs.not_paid', { count: statusCounts.not_paid })} />
                </Tabs>

                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('manageFeePaymentsPage.tableStatus.headers.studentNo')}</TableCell>
                      <TableCell>{t('manageFeePaymentsPage.tableStatus.headers.student')}</TableCell>
                      <TableCell align="right">{t('manageFeePaymentsPage.tableStatus.headers.required')}</TableCell>
                      <TableCell align="right">{t('manageFeePaymentsPage.tableStatus.headers.paid')}</TableCell>
                      <TableCell align="right">{t('manageFeePaymentsPage.tableStatus.headers.unpaid')}</TableCell>
                      <TableCell>{t('manageFeePaymentsPage.tableStatus.headers.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleStatusRows.map(r => (
                      <TableRow key={`${r.studentId}-${r.feeStructureId}`}>
                        <TableCell>{r.studentNumber || '-'}</TableCell>
                        <TableCell>{r.studentName}</TableCell>
                        <TableCell align="right">{Number(r.required || 0).toLocaleString(i18n.language || undefined)} XAF</TableCell>
                        <TableCell align="right">{Number(r.paid || 0).toLocaleString(i18n.language || undefined)} XAF</TableCell>
                        <TableCell align="right">{Number(r.unpaid || 0).toLocaleString(i18n.language || undefined)} XAF</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={r.status === 'paid' ? 'success' : r.status === 'partially_paid' ? 'warning' : 'error'}
                            label={r.status === 'paid' ? t('manageFeePaymentsPage.status.paid') : r.status === 'partially_paid' ? t('manageFeePaymentsPage.status.partially_paid') : t('manageFeePaymentsPage.status.not_paid')}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'payments' && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">{t('manageFeePaymentsPage.headers.payments')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('manageFeePaymentsPage.payments.totalPaid', { amount: totalPaid.toLocaleString(i18n.language || undefined) })}
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('manageFeePaymentsPage.tablePayments.headers.student')}</TableCell>
                  <TableCell>{t('manageFeePaymentsPage.tablePayments.headers.class')}</TableCell>
                  <TableCell>{t('manageFeePaymentsPage.tablePayments.headers.feeStructure')}</TableCell>
                  <TableCell>{t('manageFeePaymentsPage.tablePayments.headers.amount')}</TableCell>
                  <TableCell>{t('manageFeePaymentsPage.tablePayments.headers.status')}</TableCell>
                  <TableCell>{t('manageFeePaymentsPage.tablePayments.headers.date')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map(p => (
                  <TableRow key={p.id}>
                    <TableCell>{p.Student ? `${p.Student.firstName} ${p.Student.lastName}` : '-'}</TableCell>
                    <TableCell>{p.Class ? p.Class.name : '-'}</TableCell>
                    <TableCell>{p.FeeStructure ? p.FeeStructure.name : '-'}</TableCell>
                    <TableCell>{Number(p.amount).toLocaleString(i18n.language || undefined)} XAF</TableCell>
                    <TableCell><Chip size="small" color={p.status === 'completed' ? 'success' : (p.status === 'failed' ? 'error' : 'warning')} label={t(`manageFeePaymentsPage.paymentStatus.${p.status}`, { defaultValue: p.status })} /></TableCell>
                    <TableCell>{p.createdAt ? new Date(p.createdAt).toLocaleDateString(i18n.language || undefined) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {tab === 'review' && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">{t('manageFeePaymentsPage.review.title', 'Parent Payment Submissions to Review')}</Typography>
              <Button variant="outlined" size="small" onClick={loadReviewPayments}>{t('manageFeePaymentsPage.review.refresh', 'Refresh')}</Button>
            </Box>
            {reviewLoading ? (
              <Typography variant="body2" color="text.secondary">{t('manageFeePaymentsPage.review.loading', 'Loading...')}</Typography>
            ) : reviewGroups.length === 0 ? (
              <Alert severity="success">{t('manageFeePaymentsPage.review.empty', 'No payment submissions awaiting review.')}</Alert>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('manageFeePaymentsPage.review.headers.student', 'Student')}</TableCell>
                    <TableCell>{t('manageFeePaymentsPage.review.headers.fees', 'Fees')}</TableCell>
                    <TableCell align="right">{t('manageFeePaymentsPage.review.headers.total', 'Total')}</TableCell>
                    <TableCell>{t('manageFeePaymentsPage.review.headers.payer', 'Payer / Ref')}</TableCell>
                    <TableCell>{t('manageFeePaymentsPage.review.headers.proof', 'Proof')}</TableCell>
                    <TableCell>{t('manageFeePaymentsPage.review.headers.submitted', 'Submitted')}</TableCell>
                    <TableCell align="right">{t('manageFeePaymentsPage.review.headers.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviewGroups.map(g => (
                    <TableRow key={g.key}>
                      <TableCell>{g.studentName}{g.className ? ` — ${g.className}` : ''}</TableCell>
                      <TableCell>
                        {g.rows.map(r => (
                          <Typography key={r.id} variant="body2">{r.feeName}: {r.amount.toLocaleString(i18n.language || undefined)} XAF</Typography>
                        ))}
                      </TableCell>
                      <TableCell align="right">{g.total.toLocaleString(i18n.language || undefined)} XAF</TableCell>
                      <TableCell>
                        <Typography variant="body2">{g.payerName || '-'}</Typography>
                        {g.reference ? <Typography variant="caption" color="text.secondary">{g.reference}</Typography> : null}
                      </TableCell>
                      <TableCell>
                        {g.proofUrl ? (
                          <Button size="small" variant="text" component="a" href={resolveApiUrl(g.proofUrl)} target="_blank" rel="noopener">
                            {t('manageFeePaymentsPage.review.view', 'View')}
                          </Button>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{g.createdAt ? new Date(g.createdAt).toLocaleString(i18n.language || undefined) : '-'}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button size="small" variant="contained" disabled={actionBusyKey === g.key} onClick={() => openApprove(g)}>
                            {t('manageFeePaymentsPage.review.approve', 'Approve')}
                          </Button>
                          <Button size="small" variant="outlined" color="error" disabled={actionBusyKey === g.key} onClick={() => openReject(g)}>
                            {t('manageFeePaymentsPage.review.reject', 'Reject')}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={Boolean(approveGroupState)} onClose={() => !actionBusyKey && setApproveGroupState(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('manageFeePaymentsPage.review.approveTitle', 'Approve this payment')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('manageFeePaymentsPage.review.approveHint', 'Check each amount against the proof the parent sent. Correct it if it differs — the balance is worked out from it.')}
          </Typography>

          {approveGroupState?.group?.proofUrl && (
            <Button
              size="small"
              variant="outlined"
              component="a"
              href={resolveApiUrl(approveGroupState.group.proofUrl)}
              target="_blank"
              rel="noopener"
              sx={{ mb: 2 }}
            >
              {t('manageFeePaymentsPage.review.viewProof', 'View the proof')}
            </Button>
          )}

          {(approveGroupState?.rows || []).map((r, idx) => {
            const required = r.feeAmount || 0;
            const total = Number(r.paidBefore || 0) + Number(r.amount || 0);
            const leaves = required > 0 ? Math.max(required - total, 0) : null;
            // An overpayment must never read as "settled in full": the school
            // owes that money back, and the parent is told to come for it.
            const excess = required > 0 ? Math.max(total - required, 0) : 0;
            return (
              <Box key={r.id} sx={{ mb: 2 }}>
                <TextField
                  label={r.feeName}
                  type="number"
                  size="small"
                  fullWidth
                  value={r.amount}
                  onChange={(e) => {
                    const v = e.target.value;
                    setApproveGroupState(prev => ({
                      ...prev,
                      rows: prev.rows.map((x, i) => (i === idx ? { ...x, amount: v } : x)),
                    }));
                  }}
                  InputProps={{ inputProps: { min: 0 } }}
                />
                {r.paidBefore == null ? null : leaves == null ? null : (
                  <Typography variant="caption" sx={{ color: excess > 0 ? 'success.main' : leaves > 0 ? 'warning.main' : 'success.main' }}>
                    {excess > 0
                      ? t('manageFeePaymentsPage.review.refund', {
                        amount: `${excess.toLocaleString(i18n.language || undefined)} XAF`,
                        defaultValue: 'This is {{amount}} more than the fee requires. The parent will be asked to collect the refund.',
                      })
                      : leaves > 0
                        ? t('manageFeePaymentsPage.review.leaves', {
                          amount: `${leaves.toLocaleString(i18n.language || undefined)} XAF`,
                          defaultValue: 'This leaves {{amount}} outstanding on the fee.',
                        })
                        : t('manageFeePaymentsPage.review.settles', { defaultValue: 'This settles the fee in full.' })}
                  </Typography>
                )}
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveGroupState(null)} color="inherit" disabled={!!actionBusyKey}>
            {t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={!!actionBusyKey || !(approveGroupState?.rows || []).every(r => Number(r.amount) > 0)}
            onClick={() => approveGroup(approveGroupState.group, approveGroupState.rows)}
          >
            {t('manageFeePaymentsPage.review.approve', 'Approve')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('manageFeePaymentsPage.review.rejectTitle', 'Reject Payment')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('manageFeePaymentsPage.review.rejectHint', 'The parent will see this reason and can submit a corrected receipt.')}
          </Typography>
          <TextField
            label={t('manageFeePaymentsPage.review.reason', 'Reason')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>{t('manageFeePaymentsPage.review.cancel', 'Cancel')}</Button>
          <Button variant="contained" color="error" onClick={confirmReject} disabled={!!actionBusyKey}>
            {t('manageFeePaymentsPage.review.confirmReject', 'Reject Payment')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
